import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

import { STANDARD_DATE_FORMAT } from '../../../../constants/dateConfig';
import useCommonApplicationForm from '../../../../hooks/formControllers/esl/useCommonApplicationForm';
import useDocumentsSubmitForm from '../../../../hooks/formControllers/esl/useDocumentsSubmitForm';
import useAlert from '../../../../hooks/useAlert';
import useMobileDetect from '../../../../hooks/useMobileDetect';
import backend from '../../../../lib/backend';
import { isCustomError } from '../../../../lib/isCustomError';
import { createSubmitHandler } from '../../../../lib/submitHandler';
import { commonApplicationProfileAtom } from '../../../../state/atoms/commonApplicationProfile';
import cipAutocomplete from '../../../../state/autocompletes/cipAutocomplete';
import institutionAutocomplete from '../../../../state/autocompletes/institutionAutocomplete';
import commonApplicationProfileForm from '../../../../state/forms/eslForms/commonApplicationProfileForm';
import documentsSubmitForm from '../../../../state/forms/eslForms/documentsSubmitForm';
import { ApplicationAttachment } from '../../../../types/applicationAttachment';
import { EnrollmentStatus, sponsorshipDegreeTypes } from '../../../../types/commonApplicationProfile';
import { Cip, Institution } from '../../../../types/eligibility';
import { DocumentUploadValidationError, ValidationError } from '../../../../types/errors/validation';
import { ProfileCompletionStepComponentBaseProps } from '../../../../types/ProfileCompletionStep';
import {
  validateFileExtension, validateFileNameLength,
  validateFileSize,
} from '../../../esl/applicationSections/documentsSubmit/fileValidations';
import DocumentInput from '../../../esl/eslApplicationFileInput';
import AppSelect from '../../../input/AppSelect';
import Autocomplete from '../../../input/Autocomplete';
import { DEFAULT_ACCEPTED_FILE_EXTENSIONS } from '../../../input/FileInput';
import MonthPicker from '../../../input/MonthPicker';
import ProfilePopUpButtons from '../ProfilePopUpButtons';

type EducationInfoFormStepProps = ProfileCompletionStepComponentBaseProps;

// TODO: Refactor form fields into separate components
const EducationInfoFormStep: FunctionComponent<EducationInfoFormStepProps> = ({
  closeModal,
  description,
  navigateNext,
  navigatePrevious,
  sponsorshipApplication,
  title,
}) => {
  const { submitForm, setFieldsFromProfile } = useCommonApplicationForm(
    sponsorshipApplication.derived_application_type,
    EnrollmentStatus.ENROLLED, // We're assuming the user is enrolled by this point
  );
  const { showSuccessAlert, showErrorAlert } = useAlert();
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isContinueLoading, setIsContinueLoading ] = useState(false);
  const [ isSaveAndExitLoading, setIsSaveAndExitLoading ] = useState(false);
  const [ fileErrors, setFileErrors ] = useState<{ [key: string]: string }>({});
  const [ documentFiles, setDocumentFiles ] = useRecoilState(documentsSubmitForm.fields.documentFiles.valueState);
  const { uploadDocument } = useDocumentsSubmitForm(sponsorshipApplication);

  const acceptanceLetterAttachment = sponsorshipApplication.opportunity_institution_acceptance_letter_document;

  const [ commonApplicationProfile, setCommonApplicationProfile ]
    = useRecoilState(commonApplicationProfileAtom);
  const isMobile = useMobileDetect();

  const expectedGraduationDateValid = useRecoilValue(
    commonApplicationProfileForm.fields.expectedGraduationDate.validState,
  );

  const majorValid = useRecoilValue(
    commonApplicationProfileForm.fields.major.validState,
  );

  const degreeTypeValid = useRecoilValue(
    commonApplicationProfileForm.fields.degreeType.validState,
  );

  const institutionNameValid = useRecoilValue(
    commonApplicationProfileForm.fields.institution.validState,
  );

  const acceptanceLetterAttachmentValid = useMemo(() => {
    if (!acceptanceLetterAttachment) {
      return true;
    }

    const isRequiredAndMissing = acceptanceLetterAttachment?.is_required && !documentFiles[acceptanceLetterAttachment.id];
    const hasErrors = !!fileErrors[acceptanceLetterAttachment.id];

    return !(isRequiredAndMissing || hasErrors);
  }, [ acceptanceLetterAttachment, fileErrors, documentFiles ]);

  const isFormValid = useMemo(() => [
    majorValid,
    degreeTypeValid,
    institutionNameValid,
    expectedGraduationDateValid,
    acceptanceLetterAttachmentValid,
  ].every((value) => !!value), [
    majorValid,
    degreeTypeValid,
    institutionNameValid,
    expectedGraduationDateValid,
    acceptanceLetterAttachmentValid,
  ]);

  // Populate fields from the existing common app profile
  useEffect(() => {
    if (commonApplicationProfile) {
      setFieldsFromProfile(commonApplicationProfile);

      return;
    }

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const { data: commonAppProfile } = await backend.getCommonApplicationProfile();
        setFieldsFromProfile(commonAppProfile);
      } catch (err) {
        console.error(err);
        showErrorAlert('Could not load your personal information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [ commonApplicationProfile, setFieldsFromProfile, showErrorAlert ]);

  const handleSubmit = useCallback(async () => {
    try {
      const newCommonApplicationProfile = await submitForm();
      setCommonApplicationProfile(newCommonApplicationProfile);
      showSuccessAlert('Profile info updated');

      return true;
    } catch (err) {
      console.error(err);

      if (err instanceof ValidationError) {
        showErrorAlert(err.message);
      } else {
        if (isCustomError(err)) {
          const errorMessage = err?.response?.data?.detail;

          showErrorAlert(
            errorMessage
              ? errorMessage
              : `Unsuccessful profile update. Details: ${err}`,
          );
        } else {
          showErrorAlert('Unknown error occurred.');
        }
      }

      return false;
    }
  }, [
    setCommonApplicationProfile,
    showErrorAlert,
    showSuccessAlert,
    submitForm,
  ]);

  const handleFilesChange = (doc: ApplicationAttachment) => async (newFile: File | null) => {
    const attachmentId = doc.id;
    const allowedExtensions = doc.document_type.allowed_extensions || DEFAULT_ACCEPTED_FILE_EXTENSIONS;

    try {
      setFileErrors((prev) => ({ ...prev, [attachmentId]: '' }));

      if (newFile && newFile instanceof File) {
        validateFileSize(newFile);
        validateFileExtension(newFile, allowedExtensions);
        validateFileNameLength(newFile);

        await uploadDocument(doc, newFile);
      }
    } catch (error) {
      const err = error as DocumentUploadValidationError;
      setFileErrors((prev) => ({ ...prev, [attachmentId]: err.message || 'Something went wrong' }));
    }

    setDocumentFiles((prev) => ({ ...prev, [attachmentId]: newFile }));
  };

  return (
    <Box id='education-info-form'>
      <DialogTitle id='education-info-form-title'>
        <Typography align='center' variant={isMobile ? 'h3' : 'h2'}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ m: 0, mb: 4, p: 0 }}>
        <DialogContentText id='educaiton-info-form-description'>
          <Typography align='center' variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            {description}
          </Typography>
        </DialogContentText>
        <Grid container direction={'row'} mt={2} spacing={3}>
          <Grid item xs={6}>
            <Autocomplete
              autocompleteState={institutionAutocomplete}
              data-cy='school-name'
              defaultValue={commonApplicationProfile?.institution?.id}
              error={!institutionNameValid}
              field={commonApplicationProfileForm.fields.institution}
              getOptionKey={(option: Institution) => option.id}
              getOptionLabel={(option: Institution) => option?.name || ''}
              id='education-info-institution-name'
              isOptionEqualToValue={
                (option: Institution, value: Institution) =>
                  option.id === value.id
              }
              label='School Name'
              variant='outlined'
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              autocompleteState={cipAutocomplete}
              data-cy='area-of-study'
              defaultValue={commonApplicationProfile?.major?.code}
              error={!majorValid}
              field={commonApplicationProfileForm.fields.major}
              getOptionKey={(option: Cip) => option?.code}
              getOptionLabel={(option: Cip) => option?.title || ''}
              id='education-info-area-of-study'
              isOptionEqualToValue={
                (option: Cip, value: Cip) =>
                  option.code === value.code
              }
              label='Area of Study'
              variant='outlined'
            />
          </Grid>
        </Grid>
        <Grid container direction={'row'} spacing={3}>
          <Grid item xs={6}>
            <AppSelect
              dataCy='degree-level-select'
              defaultValue={commonApplicationProfile?.degree_type}
              error={!degreeTypeValid} // Ensure this is a string matching one of the DegreeType values
              field={commonApplicationProfileForm.fields.degreeType}
              formControlSx={{ marginBottom: 0 }}
              getOptionByKey={(optionKey: string) => optionKey}
              getOptionKey={(option: string) => option}
              getOptionLabel={(option: string) => option} // Accessor for the label of each option
              id='education-info-degree-level-select'
              label='Degree level'
              options={sponsorshipDegreeTypes}
              showErrorAlways
            />
          </Grid>
          <Grid item xs={6}>
            <MonthPicker
              field={commonApplicationProfileForm.fields.expectedGraduationDate}
              id='expected-graduation-date'
              label='Expected Graduation Date'
              outputDateFormat={STANDARD_DATE_FORMAT}
            />
          </Grid>
          <Grid item xs={12}>
            {
              acceptanceLetterAttachment && (
                <DocumentInput
                  doc={acceptanceLetterAttachment}
                  documentFiles={documentFiles}
                  error={fileErrors[acceptanceLetterAttachment.id]}
                  fieldErrorHighlightEnabled={!acceptanceLetterAttachmentValid}
                  handleFilesChange={handleFilesChange(acceptanceLetterAttachment)}
                  isReadonly={false}
                  isRequired={acceptanceLetterAttachment.is_required}
                  key={acceptanceLetterAttachment.id}
                />
              )
            }
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <ProfilePopUpButtons
          disabled={isLoading || isContinueLoading || isSaveAndExitLoading}
          loadingContinue={isContinueLoading}
          loadingSaveAndExit={isSaveAndExitLoading}
          onBack={() => navigatePrevious()}
          onContinue={
            async () => {
              if (!isFormValid) {
                showErrorAlert('Please fill out all the required fields');

                return;
              }

              setIsContinueLoading(true);

              try {
                const success = await handleSubmit();

                if (success) {
                  navigateNext();
                }
              } finally {
                setIsContinueLoading(false);
              }
            }
          }
          onSaveAndExit={
            createSubmitHandler({
              startLoadingState: () => setIsSaveAndExitLoading(true),
              handleSubmit,
              onSuccess: () => closeModal(),
              onFinally: () => setIsSaveAndExitLoading(false),
            })
          }
          showBack={true}
          showContinue={true}
          showSaveAndExit={isMobile}
        />
      </DialogActions>
    </Box>
  );
};

export default EducationInfoFormStep;
